import {
  PropsWithChildren,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import * as Realm from "realm-web";

interface RealmProvider {
  appId: string;
  client?: string;
  remember?: boolean;
  children: ReactNode;
}

interface RealmContextProps {
  app: Realm.App | undefined;
  error: string | void;
  login: (
    method: RealmCredentialsType,
    ...args: any
  ) => Promise<boolean>;
  logout: () => Promise<void | undefined>;
  user: Realm.User | void | undefined;
  callFunction: (func: string, ...args: any[]) => Promise<unknown>;
  customData: object;
  isLoggedIn: boolean;
  mongoClient: CachedMongoDefaultService | void | undefined;
}

export const RealmContext = createContext<RealmContextProps>(null!);

export enum RealmCredentialsType {
  anonymous,
  apiKey,
  apple,
  emailPassword,
  facebook,
  function,
  google,
  jwt,
};

export function RealmProvider({
  appId,
  client = "mongodb-atlas",
  remember = false,
  children,
}: RealmProvider) {
  const app = useMemo(() => new Realm.App({ id: appId }), [appId]);
  const [error, setError] = useState<string | void>();

  const [user, setUser] = useState<Realm.User | undefined | void>(
    (remember && app.currentUser) || undefined
  );
  const [customData, setCustomData] = useState<object>(user?.customData || {});

  const loadUser = useCallback(async function (user: Realm.User | undefined | void) {
    if (user instanceof Realm.User) {
      await user.refreshCustomData();
      setUser(user);
      setCustomData(user.customData);
    } else {
      setUser();
      setCustomData({});
    }
  }, []);

  const login = useCallback(
    async function (method: RealmCredentialsType, ...args: any): Promise<boolean> {
      const credentials = getCredentials(method, ...args);
      const [user, error] = await app
        .logIn(credentials)
        .then((user: Realm.User) => [user])
        .catch((error: Error) => [undefined, error]);

      if (error instanceof Error) {
        setError(error.toString());
        return false;
      }

      if (!(user instanceof Realm.User)) {
        setError(
          "Realm did not return an error message, but failed to log the user in."
        );
        return false;
      }

      setError();
      loadUser(user);
      return true;
    },
    [app]
  );

  const logout = useCallback(
    async function () {
      return user
        ?.logOut()
        .then(() => {
          loadUser();
        })
        .catch((error: Error) => {
          setError(error.toString());
        });
    },
    [user]
  );

  const isLoggedIn = Boolean(user?.isLoggedIn);
  const mongoClient = useMemo(() => user && new CachedMongoDefaultService(user, client), [
    user,
    client,
  ]);

  const callFunction = useCallback(async function (func: string, ...args: unknown[]): Promise<unknown> {
    return user?.functions[func](...args);
  }, [user]);

  return (
    <RealmContext.Provider
      value={{
        app,
        error,
        login,
        logout,
        user,
        callFunction,
        customData,
        isLoggedIn,
        mongoClient,
      }}
    >
      {children}
    </RealmContext.Provider>
  );
}

RealmProvider.IsLoggedIn = function IsLoggedIn ({ children }: PropsWithChildren) {
  return useContext(RealmContext).isLoggedIn ? children : null;
};

RealmProvider.IsLoggedOut = function IsLoggedOut ({ children }: PropsWithChildren) {
  return useContext(RealmContext).isLoggedIn ? null : children;
}


function getCredentials(
  type: RealmCredentialsType,
  arg0: any = undefined,
  arg1: any = undefined
): Realm.Credentials {
  switch (type) {
    case RealmCredentialsType.anonymous:
      return Realm.Credentials.anonymous(false);
    case RealmCredentialsType.apiKey:
      return Realm.Credentials.apiKey(arg0);
    case RealmCredentialsType.apple:
      return Realm.Credentials.apple(arg0);
    case RealmCredentialsType.emailPassword:
      return Realm.Credentials.emailPassword(arg0, arg1);
    case RealmCredentialsType.facebook:
      return Realm.Credentials.facebook(arg0);
    case RealmCredentialsType.function:
      return Realm.Credentials.function(arg0);
    case RealmCredentialsType.google:
      return Realm.Credentials.google(arg0);
    case RealmCredentialsType.jwt:
      return Realm.Credentials.jwt(arg0);
  }
}

type MongoDB = globalThis.Realm.Services.MongoDB;
type MongoDBDatabase = globalThis.Realm.Services.MongoDBDatabase;
type MongoDBCollection = globalThis.Realm.Services.MongoDB.MongoDBCollection<any>;

class CachedMongoDefaultService {
  private user: Realm.User;
  public defaultClient: string;
  private serviceCache: Map<string, CachedMongoService>;

  constructor (user: Realm.User, defaultClient: string) {
    this.user = user;
    this.defaultClient = defaultClient;
    this.serviceCache = new Map<string, CachedMongoService>();
  }

  service (client: string): CachedMongoService {
    if (! this.serviceCache.has(client)) {
      const service = new CachedMongoService (this.user.mongoClient(client), client);
      if (! service) {
        throw new Error (`Could not connect to mongo service ${client}`);
      }
      this.serviceCache.set(client, service);
    }

    return this.serviceCache.get(client)!;
  }

  db (name: string): CachedMongoDatabase {
    return this.service(this.defaultClient).db(name);
  }
}

class CachedMongoService {
  private service: MongoDB;
  public client: string;
  private databaseCache: Map<string, CachedMongoDatabase>;

  constructor (service: MongoDB, client: string) {
    this.service = service;
    this.client = client;
    this.databaseCache = new Map<string, CachedMongoDatabase>();
  }

  db (name: string): CachedMongoDatabase {
    if (! this.databaseCache.has(name)) {
      const database = new CachedMongoDatabase (this.service.db(name), this.client, name);
      if (! database) {
        throw new Error (`Could not connect to database ${name} on mongo service ${this.client}`);
      }
      this.databaseCache.set(name, database);
    }

    return this.databaseCache.get(name)!;
  }
}

class CachedMongoDatabase {
  private database: MongoDBDatabase;
  public client: string;
  public name: string;
  private collectionCache: Map<string, MongoDBCollection>;

  constructor (database: MongoDBDatabase, client: string, name: string) {
    this.database = database;
    this.client = client;
    this.name = name;
    this.collectionCache = new Map<string, MongoDBCollection>();
  }

  collection (name: string): MongoDBCollection {
    if (! this.collectionCache.has(name)) {
      const collection = this.database.collection(name);
      if (! collection) {
        throw new Error (`Could not connect to collection ${name} in database ${this.name} on mongo service ${this.client}`);
      }
      this.collectionCache.set(name, collection);
    }

    return this.collectionCache.get(name)!;
  }
}
